// Which revault:* keys go where: a downloaded save file, the cloud backup, or a carry code.
// Every key the app writes should be in one of these lists (test/save-keys.test.mjs checks).
// The server keeps its own copy of the device list (src/server/lib/accounts.js
// DEVICE_ONLY_SAVE_KEYS) so it drops the same keys from a cloud backup.

// A credential or a sign-in step: the account session token, a pending token handed over by the
// auth page, the sign-in return marker and the forum owner key. They never leave this browser, and
// an imported file can't set them, so restoring someone else's backup can't sign you into their
// account (docs/security/PHASE0.md). The server strips the same list (SECRET_SAVE_KEYS).
export const SECRET = ["revault:account", "revault:auth:pending", "revault:auth:return", "revault:forum:modkey"];
// Not secret, but the server's to say: a restored blob must not carry someone's supporter pass.
export const NO_SYNC = ["revault:supporter"];
// The saved tabs hold full page addresses: a save file (a file the student keeps) has them, the
// cloud backup and a carry code never do.
export const TABS = ["revault:session", "revault:session-offer"];
// About this device only: ids, counters, locks, timers and measured speed. Never in a cloud
// backup or a carry code; a save file keeps only the tabs.
export const DEVICE = [
	...TABS,
	"revault:pid", "revault:count", "revault:install", "revault:music-playing", "revault:focusing",
	"revault:island-timer", "revault:linux-restart", "revault:linux-cx-reset", "revault:linux-active",
	"revault:signin-changed", "revault:signin-route", "revault:health", "revault:helper:handoff",
	"revault:hall:toasted", "revault:hall:reloaded", "revault:onboarding",
	"revault:helper:cap", "revault:helper:w", "revault:helper:flag",
	"revault:linux-autoboot", "revault:carry-held",
];
// Per-machine locks (linux/lock.js).
export const DEVICE_PREFIXES = ["revault:linux:machine:", "revault:linux:mutate:"];
// Lists that point at files in IndexedDB, which no backup carries.
export const FILE_INDEX = [
	"revault:music", "revault:wallpaper-media", "revault:wallpaper-slots",
	"revault:linux-machines", "revault:linux-cheerpx",
];
// Study work, settings and looks: what a carry code takes by default.
export const CARRY = [
	"revault:flashcards", "revault:notes", "revault:markdown", "revault:todo", "revault:citations",
	"revault:days", "revault:totals", "revault:quests", "revault:favorites", "revault:bookmarks",
	"revault:settings", "revault:bundles", "revault:prev-look", "revault:home",
	"revault:tool-recents", "revault:book-recents", "revault:worldclock", "revault:typerace",
	// The school-day streak travels with backups and carry codes; one that arrives from another
	// browser is told apart by rv-streak-device (outside "revault:", so it never leaves) and says
	// "Welcome back" (shared/streak.js). Calendar events are study work too.
	"revault:streak", "revault:calendar",
	"revault:helper", // only the FIELD_FILTER fields
];
// Keys where only some fields leave the device. revault:helper also holds the fair-use pass, the
// models in this device's cache and this device's speed, which mean nothing anywhere else.
export const FIELD_FILTER = {
	"revault:helper": ["engine", "history", "level", "lang", "readAloud", "counts", "altH", "intro"],
};
// "Also include my history" on a carry code.
export const CARRY_OPTIONAL = ["revault:history", "revault:read-pos"];
// Known, and fine in a save file and the cloud backup as before, but not carried.
export const OTHER = [
	"revault:wallpaper-image", "revault:lockedLook", "revault:lastFreeLook", "revault:lookReverted",
	"revault:island-tip", "revault:release-seen", "revault:onboarded", "revault:safari-saves",
	"revault:hall:used", "revault:hall:invite", "revault:hall:u13", "revault:hall:sound", "revault:hall:room",
	"revault:hall:qotd-hidden", "revault:hall:how", "revault:hall:density",
];

export const isSecret = (k) => SECRET.includes(k) || NO_SYNC.includes(k);
export const isDevice = (k) => DEVICE.includes(k) || DEVICE_PREFIXES.some((p) => k.startsWith(p));

// "secret" | "device" | "fileIndex" | "carry" | "carryOpt" | "other"
export function scopeOf(key) {
	if (isSecret(key)) return "secret";
	if (isDevice(key)) return "device";
	if (FILE_INDEX.includes(key)) return "fileIndex";
	if (CARRY.includes(key)) return "carry";
	if (CARRY_OPTIONAL.includes(key)) return "carryOpt";
	return "other";
}

// Does `key` go in a blob for `scope` ("file" | "cloud")? Carry comes with the carry code.
export function keepFor(key, scope) {
	if (isSecret(key)) return false;
	if (isDevice(key)) return scope === "file" && TABS.includes(key);
	return true;
}

// Keep only the fields of `raw` (a stored JSON string) that may leave the device. Anything that
// doesn't parse to an object goes as an empty object.
export function filterFields(key, raw) {
	const keep = FIELD_FILTER[key];
	if (!keep) return raw;
	let v = null;
	try { v = JSON.parse(raw); } catch (e) { v = null; }
	const out = {};
	if (v && typeof v === "object" && !Array.isArray(v)) for (const f of keep) if (Object.prototype.hasOwnProperty.call(v, f)) out[f] = v[f];
	return JSON.stringify(out);
}

// Restoring a field-filtered key: the incoming fields replace the same fields here, and the rest
// (this device's own pass, models and speed) stays as it is.
export function mergeFields(key, localRaw, incomingRaw) {
	const parse = (s) => { try { const v = JSON.parse(s); return v && typeof v === "object" && !Array.isArray(v) ? v : {}; } catch (e) { return {}; } };
	return JSON.stringify(Object.assign(parse(localRaw), parse(filterFields(key, incomingRaw))));
}
